import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import {
  AlertCircle,
  AlertTriangle,
  Edit,
  Megaphone,
  Plus,
  Trash2,
  Wrench,
  Info,
  X,
} from 'lucide-react';
import { auth, db } from '../../firebase';
import { EcointelLottieMark } from '../../components/EcoIntelLoader';
import { useUserFacingError } from '../../hooks/useUserFacingError';
import {
  ANNOUNCEMENTS_COLLECTION,
  ANNOUNCEMENT_VARIANTS,
  ANNOUNCEMENT_VARIANT_LABELS,
  DEFAULT_ANNOUNCEMENT,
  resolveAnnouncement,
} from '../../constants/announcements';
import AnnouncementBanner from '../../components/AnnouncementBanner';
import AdminTable from '../../components/admin/AdminTable';
import UserTargetPicker, { resolveTargetUids } from '../../components/admin/UserTargetPicker';
import { useAdminData } from '../../components/admin/AdminLayout';

const VARIANT_ICON = {
  info: Info,
  warning: Megaphone,
  critical: AlertTriangle,
  maintenance: Wrench,
};

function isoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(local) {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function emptyDraft() {
  return {
    enabled: DEFAULT_ANNOUNCEMENT.enabled,
    eyebrow: DEFAULT_ANNOUNCEMENT.eyebrow,
    message: DEFAULT_ANNOUNCEMENT.message,
    variant: DEFAULT_ANNOUNCEMENT.variant,
    ctaLabel: DEFAULT_ANNOUNCEMENT.ctaLabel,
    ctaUrl: DEFAULT_ANNOUNCEMENT.ctaUrl,
    dismissible: DEFAULT_ANNOUNCEMENT.dismissible,
    priority: DEFAULT_ANNOUNCEMENT.priority,
    startsAt: DEFAULT_ANNOUNCEMENT.startsAt,
    endsAt: DEFAULT_ANNOUNCEMENT.endsAt,
    target: {
      mode: DEFAULT_ANNOUNCEMENT.target.mode,
      uids: [],
      segments: {
        roles: [],
        planTiers: [],
        subscriptionStatuses: [],
        hasFarms: null,
      },
    },
  };
}

function AnnouncementEditor({ initial, onClose, onSaved, users }) {
  const { formatError, logError } = useUserFacingError();
  const [draft, setDraft] = useState(() => initial || emptyDraft());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setDraft(initial || emptyDraft());
  }, [initial]);

  const previewAnnouncement = useMemo(() => ({
    id: 'preview',
    ...draft,
    version: 1,
  }), [draft]);

  const handleSave = async () => {
    if (!draft.message?.trim()) {
      setError('Message is required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const id = initial?.id || doc(collection(db, ANNOUNCEMENTS_COLLECTION)).id;
      const resolvedUids = draft.target.mode === 'selected'
        ? resolveTargetUids({ users, manualUids: draft.target.uids || [], segments: draft.target.segments || {} })
        : [];

      const prevVersion = Number(initial?.version) || 0;
      const nextVersion = prevVersion + 1;

      const payload = {
        enabled: !!draft.enabled,
        eyebrow: (draft.eyebrow || '').trim().slice(0, 24),
        message: draft.message.trim(),
        variant: ANNOUNCEMENT_VARIANTS.includes(draft.variant) ? draft.variant : DEFAULT_ANNOUNCEMENT.variant,
        ctaLabel: (draft.ctaLabel || '').trim(),
        ctaUrl: (draft.ctaUrl || '').trim(),
        dismissible: !!draft.dismissible,
        priority: Number(draft.priority) || DEFAULT_ANNOUNCEMENT.priority,
        startsAt: draft.startsAt || null,
        endsAt: draft.endsAt || null,
        version: nextVersion,
        target: {
          mode: draft.target.mode === 'selected' ? 'selected' : 'all',
          uids: draft.target.mode === 'selected' ? resolvedUids : [],
          segments: {
            roles: draft.target.segments?.roles || [],
            planTiers: draft.target.segments?.planTiers || [],
            subscriptionStatuses: draft.target.segments?.subscriptionStatuses || [],
            hasFarms: draft.target.segments?.hasFarms ?? null,
          },
        },
        createdBy: initial?.createdBy || auth.currentUser?.uid || null,
        createdAt: initial?.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedByEmail: auth.currentUser?.email || null,
      };
      await setDoc(doc(db, ANNOUNCEMENTS_COLLECTION, id), payload, { merge: true });
      onSaved();
    } catch (e) {
      logError('Save announcement failed', e);
      setError(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h3 className="font-bold text-lg flex items-center gap-2 text-slate-800">
            <Megaphone size={18} className="text-slate-500" />
            {initial ? 'Edit announcement' : 'New announcement'}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-5">
          {error ? (
            <div className="flex items-start gap-2 p-3 rounded-lg border border-rose-200 bg-rose-50 text-rose-800 text-sm">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              <div>{error}</div>
            </div>
          ) : null}

          {/* Live preview */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Preview</label>
            <div className="rounded-lg overflow-hidden border border-slate-200">
              <AnnouncementBanner announcement={previewAnnouncement} isPreview />
            </div>
          </div>

          {/* Eyebrow (optional all-caps prefix, e.g. "WHAT'S NEW") */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Eyebrow</label>
            <input
              type="text"
              value={draft.eyebrow || ''}
              onChange={(e) => setDraft((d) => ({ ...d, eyebrow: e.target.value.slice(0, 24) }))}
              className="w-full border border-slate-200 rounded-lg p-2 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
              placeholder="Optional, e.g. WHAT'S NEW"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Message *</label>
            <textarea
              value={draft.message}
              onChange={(e) => setDraft((d) => ({ ...d, message: e.target.value.slice(0, 500) }))}
              rows={3}
              className="w-full border border-slate-200 rounded-lg p-2.5 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
              placeholder="What do you want users to know?"
            />
            <div className="text-[10px] text-slate-400 text-right mt-0.5">{(draft.message || '').length}/500</div>
          </div>

          {/* Variant + CTA */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Variant</label>
              <div className="grid grid-cols-2 gap-1.5">
                {ANNOUNCEMENT_VARIANTS.map((v) => {
                  const Icon = VARIANT_ICON[v] || Megaphone;
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setDraft((d) => ({ ...d, variant: v }))}
                      className={`text-[11px] px-2 py-1.5 rounded border inline-flex items-center gap-1 ${
                        draft.variant === v
                          ? 'bg-[#043132] border-[#043132] text-white'
                          : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      <Icon size={11} /> {ANNOUNCEMENT_VARIANT_LABELS[v]}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">CTA label</label>
              <input
                type="text"
                value={draft.ctaLabel || ''}
                onChange={(e) => setDraft((d) => ({ ...d, ctaLabel: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg p-2 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
                placeholder="e.g. Learn more"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">CTA URL</label>
              <input
                type="text"
                value={draft.ctaUrl || ''}
                onChange={(e) => setDraft((d) => ({ ...d, ctaUrl: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg p-2 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
                placeholder="/dashboard or https://…"
              />
            </div>
          </div>

          {/* Schedule + priority */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Show from (optional)</label>
              <input
                type="datetime-local"
                value={isoToLocalInput(draft.startsAt)}
                onChange={(e) => setDraft((d) => ({ ...d, startsAt: localInputToIso(e.target.value) }))}
                className="w-full border border-slate-200 rounded-lg p-2 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Show until (optional)</label>
              <input
                type="datetime-local"
                value={isoToLocalInput(draft.endsAt)}
                onChange={(e) => setDraft((d) => ({ ...d, endsAt: localInputToIso(e.target.value) }))}
                className="w-full border border-slate-200 rounded-lg p-2 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Priority (0–100)</label>
              <input
                type="number"
                min={0}
                max={100}
                value={draft.priority}
                onChange={(e) => setDraft((d) => ({ ...d, priority: Number(e.target.value) }))}
                className="w-full border border-slate-200 rounded-lg p-2 text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
              />
              <p className="text-[10px] text-slate-400 mt-1">Higher priority wins when multiple match the same user.</p>
            </div>
          </div>

          {/* Toggles */}
          <div className="flex items-center gap-5 flex-wrap">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={draft.enabled}
                onChange={(e) => setDraft((d) => ({ ...d, enabled: e.target.checked }))}
                className="rounded border-slate-300 text-[#043132] focus:ring-[#043132]"
              />
              <span className="font-medium text-slate-700">Enabled (publish to users)</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={draft.dismissible}
                onChange={(e) => setDraft((d) => ({ ...d, dismissible: e.target.checked }))}
                className="rounded border-slate-300 text-[#043132] focus:ring-[#043132]"
              />
              <span className="font-medium text-slate-700">Dismissible</span>
            </label>
          </div>

          {/* Targeting */}
          <div className="border-t border-slate-100 pt-5">
            <h4 className="text-sm font-semibold text-slate-800 mb-2">Audience</h4>
            <UserTargetPicker
              value={draft.target}
              onChange={(target) => setDraft((d) => ({ ...d, target }))}
              users={users}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-100">
          <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 text-slate-500 hover:text-slate-700 text-sm font-medium disabled:opacity-50">Cancel</button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 bg-[#043132] text-white rounded-lg hover:bg-[#065354] font-medium text-sm disabled:opacity-50 inline-flex items-center gap-2"
          >
            {saving ? <><EcointelLottieMark size={14} /> Saving…</> : 'Save announcement'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AnnouncementsAdmin() {
  const { users } = useAdminData();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [variantFilter, setVariantFilter] = useState([]);
  const [stateFilter, setStateFilter] = useState([]);
  const [modeFilter, setModeFilter] = useState([]);

  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, ANNOUNCEMENTS_COLLECTION),
      (snap) => {
        const out = snap.docs.map((d) => resolveAnnouncement(d.id, d.data()));
        out.sort((a, b) => {
          if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
          if (b.priority !== a.priority) return b.priority - a.priority;
          const aT = a.updatedAt ? Date.parse(a.updatedAt) : 0;
          const bT = b.updatedAt ? Date.parse(b.updatedAt) : 0;
          return bT - aT;
        });
        setRows(out);
        setLoading(false);
      },
      (err) => {
        console.warn('[AnnouncementsAdmin] listener error:', err?.message || err);
        setRows([]);
        setLoading(false);
      },
    );
    return () => unsub();
  }, []);

  const handleNew = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  const handleEdit = (row) => {
    setEditing(row);
    setEditorOpen(true);
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete announcement "${(row.message || '').slice(0, 60)}…"?`)) return;
    try {
      await deleteDoc(doc(db, ANNOUNCEMENTS_COLLECTION, row.id));
    } catch (e) {
      alert(`Delete failed: ${e?.message || 'unknown error'}`);
    }
  };

  const handleToggleEnabled = async (row) => {
    try {
      await setDoc(
        doc(db, ANNOUNCEMENTS_COLLECTION, row.id),
        {
          enabled: !row.enabled,
          version: (Number(row.version) || 1) + (row.enabled ? 0 : 1),
          updatedAt: serverTimestamp(),
          updatedByEmail: auth.currentUser?.email || null,
        },
        { merge: true },
      );
    } catch (e) {
      alert(`Toggle failed: ${e?.message || 'unknown error'}`);
    }
  };

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (variantFilter.length && !variantFilter.includes(r.variant)) return false;
      if (modeFilter.length && !modeFilter.includes(r.target.mode)) return false;
      if (stateFilter.length) {
        const state = r.enabled ? 'enabled' : 'disabled';
        if (!stateFilter.includes(state)) return false;
      }
      return true;
    });
  }, [rows, variantFilter, modeFilter, stateFilter]);

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3 text-slate-900">
            <Megaphone size={22} /> Announcements
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Site-wide and user-targeted notifications. The highest-priority enabled announcement
            overrides the promo banner in the same on-page position.
          </p>
        </div>
        <button
          onClick={handleNew}
          className="bg-[#043132] text-white px-4 py-2.5 rounded-lg flex items-center gap-2 hover:bg-[#065354] font-medium text-sm transition-colors"
        >
          <Plus size={16} /> New announcement
        </button>
      </div>

      <AdminTable
        rows={filteredRows}
        loading={loading}
        rowKey={(r) => r.id}
        searchPlaceholder="Search by message or CTA…"
        searchPredicate={(r, q) => (
          (r.message || '').toLowerCase().includes(q) ||
          (r.ctaLabel || '').toLowerCase().includes(q) ||
          (r.ctaUrl || '').toLowerCase().includes(q)
        )}
        filterGroups={[
          {
            id: 'state',
            label: 'State',
            selected: stateFilter,
            onChange: setStateFilter,
            options: [
              { value: 'enabled', label: 'Enabled' },
              { value: 'disabled', label: 'Disabled' },
            ],
          },
          {
            id: 'variant',
            label: 'Variant',
            selected: variantFilter,
            onChange: setVariantFilter,
            options: ANNOUNCEMENT_VARIANTS.map((v) => ({ value: v, label: ANNOUNCEMENT_VARIANT_LABELS[v] })),
          },
          {
            id: 'mode',
            label: 'Audience',
            selected: modeFilter,
            onChange: setModeFilter,
            options: [
              { value: 'all', label: 'All users' },
              { value: 'selected', label: 'Selected users' },
            ],
          },
        ]}
        defaultPageSize={25}
        emptyMessage="No announcements yet. Click ‘New announcement’ to create one."
        columns={[
          { key: 'state', label: 'State', sortable: false },
          { key: 'message', label: 'Message', sortValue: (r) => (r.message || '').toLowerCase() },
          { key: 'variant', label: 'Variant', sortValue: (r) => r.variant },
          { key: 'audience', label: 'Audience', sortable: false },
          { key: 'priority', label: 'Priority', sortValue: (r) => r.priority, align: 'right', className: 'text-right' },
          { key: 'updated', label: 'Updated', sortValue: (r) => (r.updatedAt ? Date.parse(r.updatedAt) || 0 : 0) },
          { key: 'actions', label: 'Actions', align: 'right', sortable: false, className: 'text-right' },
        ]}
        renderRow={(r) => {
          const Icon = VARIANT_ICON[r.variant] || Megaphone;
          const audienceLabel = r.target.mode === 'all'
            ? 'All users'
            : `${r.target.uids.length} user${r.target.uids.length === 1 ? '' : 's'}`;
          return (
            <>
              <td className="p-4">
                <button
                  type="button"
                  onClick={() => handleToggleEnabled(r)}
                  className={`text-[10px] px-1.5 py-0.5 rounded font-medium border ${
                    r.enabled
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-slate-100 text-slate-500 border-slate-200'
                  }`}
                  title={r.enabled ? 'Click to disable' : 'Click to enable (bumps version, re-shows to dismissed users)'}
                >
                  {r.enabled ? 'Enabled' : 'Disabled'}
                </button>
              </td>
              <td className="p-4">
                <div className="text-sm text-slate-800 line-clamp-2 max-w-md">{r.message || <span className="italic text-slate-400">no message</span>}</div>
                {r.ctaLabel ? (
                  <div className="text-[10px] text-slate-400 mt-1">CTA: {r.ctaLabel} → {r.ctaUrl}</div>
                ) : null}
              </td>
              <td className="p-4">
                <span className="text-[10px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-medium bg-slate-100 text-slate-700">
                  <Icon size={10} /> {ANNOUNCEMENT_VARIANT_LABELS[r.variant] || r.variant}
                </span>
              </td>
              <td className="p-4 text-sm text-slate-700">{audienceLabel}</td>
              <td className="p-4 text-right text-sm tabular-nums text-slate-700">{r.priority}</td>
              <td className="p-4 text-xs text-slate-500 whitespace-nowrap">
                {r.updatedAt ? new Date(r.updatedAt).toLocaleString() : '—'}
                {r.updatedByEmail ? <div className="text-[10px] text-slate-400 truncate max-w-[160px]">{r.updatedByEmail}</div> : null}
              </td>
              <td className="p-4 text-right whitespace-nowrap">
                <button onClick={() => handleEdit(r)} className="mr-2 p-1.5 text-slate-400 hover:text-[#043132] rounded hover:bg-slate-100" title="Edit"><Edit size={16} /></button>
                <button onClick={() => handleDelete(r)} className="p-1.5 text-slate-400 hover:text-red-600 rounded hover:bg-red-50" title="Delete"><Trash2 size={16} /></button>
              </td>
            </>
          );
        }}
      />

      {editorOpen ? (
        <AnnouncementEditor
          initial={editing}
          users={users || []}
          onClose={() => { setEditorOpen(false); setEditing(null); }}
          onSaved={() => { setEditorOpen(false); setEditing(null); }}
        />
      ) : null}
    </>
  );
}
